import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { isNil } from 'ramda';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useActualFormProperties } from './utils';

import { LoadingButton } from '@mui/lab';

import MultiStepForm from '../../../../shared/MultiStepForm/MultiStepForm';
import {
  ADMIN_REPORTS,
  ADVANCED_REPORTS,
  REPORT_TYPES,
  SchemaTypes
} from '../type';
import SelectReportType from './SelectReportType';
import { useGetNewReport } from './hooks/useGetNewReport';
import dayjs from 'dayjs';

type GeneratedReportFormProps = {
  forcedDefaultReportType?: REPORT_TYPES;
  handleClose: () => void;
};

export const BasicReportForm = ({
  handleClose,
  forcedDefaultReportType
}: GeneratedReportFormProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'adminPortal.procedures.reports'
  });

  const [formType, setFormType] = useState<REPORT_TYPES>();
  const { schema, Component } = useActualFormProperties(
    formType,
    forcedDefaultReportType
  );

  const hasReferenceDate = Object.keys(schema.fields).includes(
    'reference_date'
  );
  const { mutateAsync: saveForm, isLoading } = useGetNewReport({
    reportTypes: formType
  });

  const methods = useForm<SchemaTypes>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...(hasReferenceDate
        ? { reference_date: dayjs().format('DD-MM-YYYY') }
        : {})
    }
  });

  const onSubmit = async (data) => {
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined)
    ); // removes undefined values from data
    await saveForm(filteredData);
    handleClose();
  };

  return (
    <MultiStepForm methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
      <MultiStepForm.Section title={t('createReport')}>
        <SelectReportType
          forcedDefaultReportType={forcedDefaultReportType}
          setFormType={setFormType}
        />
      </MultiStepForm.Section>
      {formType &&
        formType !== ADVANCED_REPORTS.PUBLISH_REPORT &&
        formType !== ADMIN_REPORTS.BUH_SETTINGS &&
        formType !== ADMIN_REPORTS.ACCOUNT_GROUPS_REPORT &&
        formType !== ADMIN_REPORTS.BALANCE_BUDGET_OVERVIEW && (
          <MultiStepForm.Section title={t('parameters')}>
            <Component />
          </MultiStepForm.Section>
        )}
      <MultiStepForm.Actions>
        <LoadingButton
          disabled={isNil(formType)}
          loading={isLoading}
          type="submit"
          variant="contained"
          autoFocus
          color="dark"
        >
          {t('generateNew')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
